import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isConnectorAvailable, getRenderers } from '../lib/connector';

const STORAGE_KEY = 'maestro_local_network_banner_dismissed_at';
const DISMISS_MS = 6 * 60 * 60 * 1000;

const STEPS = [
  'Apple menu → System Settings → Privacy & Security → Local Network → turn Maestro off, then back on.',
  'Click Maestro in the menu bar → Quit Maestro.',
  'Open Maestro again from Applications or the Dock.',
];

function isMacConnectorHost() {
  if (!isConnectorAvailable()) return false;
  const ua = navigator.userAgent || '';
  if (/Macintosh|Mac OS X/i.test(ua)) return true;
  const plat = navigator.platform || '';
  return /Mac/i.test(plat);
}

function networkRendererCount(renderers) {
  if (!Array.isArray(renderers)) return 0;
  return renderers.filter((r) => {
    const id = String(r?.id || r?.renderer_id || '');
    const ip = String(r?.ip || r?.host || '');
    if (id.includes('127.0.0.1') || ip === '127.0.0.1') return false;
    if (/usb dac/i.test(String(r?.name || '')) && !ip) return false;
    return true;
  }).length;
}

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const t = Number(raw);
    return Number.isFinite(t) && Date.now() - t < DISMISS_MS;
  } catch {
    return false;
  }
}

/**
 * #1608 — macOS Local Network grant can go stale after a Connector update (SSDP os error 65 /
 * zero-response DiscoveryHealth). Connector emits maestro://discovery-degraded; it does not
 * surface os error 65 to the web layer on v11.0.338.
 */
export default function LocalNetworkRediscoveryBanner() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => wasDismissedRecently());
  const unlisteners = useRef([]);
  const degradedRef = useRef(false);

  const evaluateRenderers = useCallback(async () => {
    if (!degradedRef.current || dismissed) return;
    try {
      const renderers = await getRenderers();
      if (networkRendererCount(renderers) > 0) {
        degradedRef.current = false;
        setVisible(false);
      } else {
        setVisible(true);
      }
    } catch {
      if (degradedRef.current) setVisible(true);
    }
  }, [dismissed]);

  useEffect(() => {
    if (!isMacConnectorHost() || dismissed) return undefined;

    const tauri = window.__TAURI__;
    if (!tauri?.event?.listen) return undefined;
    const listen = tauri.event.listen.bind(tauri.event);

    const showFromPayload = (payload) => {
      if (payload?.kind === 'local_network' || payload?.local_network_stale_grant) {
        degradedRef.current = true;
        setVisible(true);
        evaluateRenderers();
        return;
      }
      const n = payload?.consecutive_zero_responses;
      if (typeof n === 'number' && n < 5) return;
      degradedRef.current = true;
      setVisible(true);
      evaluateRenderers();
    };

    const setup = async () => {
      unlisteners.current.push(await listen('maestro://discovery-degraded', (e) => showFromPayload(e?.payload)));
      unlisteners.current.push(await listen('macos-permission-required', (e) => showFromPayload(e?.payload)));
    };
    setup();

    const interval = setInterval(() => {
      if (degradedRef.current) evaluateRenderers();
    }, 45000);

    return () => {
      unlisteners.current.forEach((fn) => {
        if (typeof fn === 'function') fn();
      });
      unlisteners.current = [];
      clearInterval(interval);
    };
  }, [dismissed, evaluateRenderers]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch { /* noop */ }
    setDismissed(true);
    setVisible(false);
  }, []);

  if (!visible || dismissed) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2147483645,
        background: '#3d2e0a',
        color: '#f5f5f7',
        borderBottom: '1px solid #c9a84c',
        boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
        padding: '12px 16px 14px',
        font: '14px/1.45 -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <strong style={{ color: '#c9a84c' }}>Network players missing after an update?</strong>
        <p style={{ margin: '8px 0 10px' }}>
          macOS sometimes keeps Local Network turned on for Maestro but blocks discovery until you
          refresh that permission. Try these steps in order:
        </p>
        <ol style={{ margin: '0 0 10px 18px', padding: 0 }}>
          {STEPS.map((step) => (
            <li key={step} style={{ marginBottom: 6 }}>{step}</li>
          ))}
        </ol>
        <button
          type="button"
          onClick={dismiss}
          style={{
            background: 'transparent',
            border: '1px solid #c9a84c66',
            color: '#f5f5f7',
            borderRadius: 6,
            padding: '4px 10px',
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          Dismiss for now
        </button>
      </div>
    </div>
  );
}
